import { useId } from 'react';
import { ColourSwatchSelector } from '@/features/shared/components/ColourSwatchSelector';
import {
  colourSwatchSelectorControlSize,
  colourSwatchSelectorWidth,
} from '@/features/shared/components/colourSwatchSelectorMetrics';
import {
  normalizeProjectBadgeChars,
  projectBadgeCharsMaxLength,
  projectBadgeColorOptions,
} from '@/shared/data/projectBadges';
import { s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Checkbox, FormHelperText, Text, TextField } from '@/ui/primitives';
import { formFieldLabelSx } from '@/ui/primitives/formFieldStyles';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ProjectBadgeColor } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectBadgeFieldProps = {
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  chars: string;
  color: ProjectBadgeColor;
  onCharsChange: (value: string) => void;
  onColorChange: (value: ProjectBadgeColor) => void;
  disabled?: boolean;
  textFieldColumnWidth?: string;
  checkboxMarginLeft?: string;
  preview?: ReactElement;
};

const checkboxColumnWidth = `calc(${s('xl')} + ${s('md')})`;

const projectBadgeHelperTextSx = {
  mt: s('xxs'),
  fontSize: '13px',
} as const;
const projectBadgeLabelSx = [formFieldLabelSx, { fontWeight: 400 }] as SxProps<Theme>;

export const ProjectBadgeField = ({
  enabled,
  onEnabledChange,
  chars,
  color,
  onCharsChange,
  onColorChange,
  disabled = false,
  textFieldColumnWidth = 'minmax(0, 1fr)',
  checkboxMarginLeft = s('none'),
  preview,
}: ProjectBadgeFieldProps): ReactElement => {
  const fieldId = useId();
  const controlsDisabled = disabled || !enabled;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${textFieldColumnWidth} ${colourSwatchSelectorWidth} auto ${checkboxColumnWidth}`,
        alignItems: 'start',
        columnGap: s('sm'),
        rowGap: s('sm'),
        width: 'fit-content',
        maxWidth: '100%',
      }}
    >
      <Text role="body" component="label" htmlFor={fieldId} sx={projectBadgeLabelSx}>
        Badge
      </Text>
      <Box />
      <Box />
      <Box />
      <TextField
        id={fieldId}
        fieldStyle="settings"
        inputHeight={32}
        value={chars}
        placeholder="P"
        slotProps={{ htmlInput: { maxLength: projectBadgeCharsMaxLength } }}
        disabled={controlsDisabled}
        onChange={(event) => {
          onCharsChange(normalizeProjectBadgeChars(event.target.value));
        }}
      />
      <ColourSwatchSelector
        ariaLabel="Choose project badge colour"
        disabled={controlsDisabled}
        value={color}
        options={projectBadgeColorOptions}
        onChange={onColorChange}
      />
      {preview ? (
        <Box
          sx={{
            height: colourSwatchSelectorControlSize,
            display: 'flex',
            alignItems: 'center',
            ml: s('xs'),
          }}
        >
          {preview}
        </Box>
      ) : (
        <Box />
      )}
      <Box
        sx={{
          width: checkboxColumnWidth,
          minWidth: checkboxColumnWidth,
          maxWidth: checkboxColumnWidth,
          height: colourSwatchSelectorControlSize,
          minHeight: colourSwatchSelectorControlSize,
          maxHeight: colourSwatchSelectorControlSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          ml: checkboxMarginLeft,
        }}
      >
        <Checkbox
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onEnabledChange(event.target.checked);
          }}
          slotProps={{ input: { 'aria-label': 'Enable project badge' } }}
          sx={{
            p: s('none'),
            width: '24px',
            minWidth: '24px',
            maxWidth: '24px',
            height: '24px',
            minHeight: '24px',
            maxHeight: '24px',
            '& .MuiSvgIcon-root': {
              fontSize: '24px',
            },
          }}
        />
      </Box>
      <FormHelperText sx={projectBadgeHelperTextSx}>Up to 2 characters.</FormHelperText>
      <Box />
      <Box />
      <Box />
    </Box>
  );
};
